/**
 * Quixo OS dashboard kit (v2.2, 8 Oct 2026): Card, KpiTile, Delta, ScoreRing, StatusChip, Rank, IconTile, Avatar,
 * ActionTile, PromoCard, Skeleton. Presentational only; every number is passed in already formatted.
 * Colour is never the only signal: deltas carry an arrow, statuses carry a label.
 */
import type { AnchorHTMLAttributes, HTMLAttributes, ReactNode } from 'react';
import { Heading } from './Typography';
import { Sparkline } from './Charts';

const cx = (...parts: (string | undefined | false)[]) => parts.filter(Boolean).join(' ');

export function Card(props: HTMLAttributes<HTMLElement>) {
  return <section {...props} className={cx('qm-card', props.className)} />;
}

/** Card header: optional leading icon tile, title with an optional sub-line, trailing slot (link, select). */
export function CardHead({ id, title, sub, icon, trailing }: { id?: string; title: ReactNode; sub?: ReactNode; icon?: ReactNode; trailing?: ReactNode }) {
  return (
    <div className="qm-card__head">
      <div className="qm-card__title">
        {icon ? <IconTile size="sm">{icon}</IconTile> : null}
        <Heading level={2} id={id}>{title}{sub ? <small>{sub}</small> : null}</Heading>
      </div>
      {trailing ?? null}
    </div>
  );
}

export type DeltaDir = 'up' | 'down' | 'same';
/** Change indicator. `good` says whether the direction is desirable (an increase in bounce rate is not). */
export function Delta({ dir, good, children }: { dir: DeltaDir; good?: boolean; children: ReactNode }) {
  return <span className="qm-delta" data-dir={dir} data-good={good === undefined ? undefined : String(good)}>{children}</span>;
}

/** KPI tile: pastel icon + label, oversized value, delta + caption, sparkline. `empty` replaces the body with a message. */
export function KpiTile({ id, label, icon, tone, value, delta, caption, spark, empty, href, linkLabel }: {
  id: string; label: ReactNode; icon?: ReactNode; tone?: IconTone; value: ReactNode; delta?: ReactNode; caption?: ReactNode; spark?: readonly number[]; empty?: ReactNode; href?: string; linkLabel?: ReactNode;
}) {
  return (
    <Card className="qm-kpi" aria-labelledby={id}>
      <p className="qm-kpi__head" id={id}>{icon ? <IconTile size="sm" tone={tone}>{icon}</IconTile> : null}<span>{label}</span></p>
      {empty ? (
        <p className="qm-kpi__empty">{empty}{href && linkLabel ? <> <a className="qm-card__link" href={href}>{linkLabel}</a></> : null}</p>
      ) : (
        <div className="qm-kpi__body">
          <div className="qm-kpi__value">
            <span className="qm-kpi__number">{value}</span>
            <span className="qm-kpi__meta">{delta}{caption ? <span>{caption}</span> : null}</span>
          </div>
          {spark && spark.length > 1 ? <span className="qm-kpi__spark"><Sparkline values={spark} /></span> : null}
        </div>
      )}
    </Card>
  );
}

/** 0-100 ring with the value inside; exposed as a meter. */
export function ScoreRing({ value, max = 100, label, size = 'md', caption }: { value: number; max?: number; label: string; size?: 'md' | 'lg'; caption?: ReactNode }) {
  const v = Math.max(0, Math.min(max, value));
  const r = 42; const c = 2 * Math.PI * r;
  return (
    <span className="qm-ring" data-size={size} role="meter" aria-label={label} aria-valuemin={0} aria-valuemax={max} aria-valuenow={Math.round(v)}>
      <svg viewBox="0 0 100 100" aria-hidden="true" focusable="false">
        <circle className="qm-ring__track" cx="50" cy="50" r={r} />
        <circle className="qm-ring__arc" cx="50" cy="50" r={r} strokeDasharray={`${(v / max) * c} ${c}`} />
      </svg>
      <span className="qm-ring__label" aria-hidden="true"><b>{Math.round(v)}</b>{caption ? <small>{caption}</small> : null}</span>
    </span>
  );
}

export type StatusTone = 'accent' | 'positive' | 'warning' | 'negative' | 'info' | 'neutral';
export function StatusChip({ tone = 'accent', children }: { tone?: StatusTone; children: ReactNode }) {
  return <span className="qm-status" data-tone={tone}>{children}</span>;
}

export function Rank({ n, tone }: { n: number; tone?: 'accent' | 'warning' | 'neutral' }) {
  return <span className="qm-rank" data-tone={tone} aria-hidden="true">{n}</span>;
}

export type IconTone = 'accent' | 'coral' | 'sun' | 'sky' | 'mint';
export function IconTile({ tone = 'accent', size = 'md', children }: { tone?: IconTone; size?: 'sm' | 'md'; children: ReactNode }) {
  return <span className="qm-icontile" data-tone={tone === 'accent' ? undefined : tone} data-size={size} aria-hidden="true">{children}</span>;
}

export function Avatar({ name }: { name: string }) {
  const initials = name.split(/[\s._@-]+/).filter(Boolean).slice(0, 2).map((p) => [...p][0]!.toUpperCase()).join('') || '?';
  return <span className="qm-avatar" aria-hidden="true">{initials}</span>;
}

/** Quick-action tile: icon, title, one-line description. Renders as a link. */
export function ActionTile({ icon, title, desc, ...rest }: AnchorHTMLAttributes<HTMLAnchorElement> & { icon: ReactNode; title: ReactNode; desc?: ReactNode }) {
  return (
    <a {...rest} className={cx('qm-action', rest.className)}>
      <IconTile size="sm">{icon}</IconTile>
      <b>{title}</b>
      {desc ? <small>{desc}</small> : null}
    </a>
  );
}

/** The one accent moment per dashboard: dark card with lavender light and a CTA. */
export function PromoCard({ id, title, body, action }: { id: string; title: ReactNode; body: ReactNode; action: ReactNode }) {
  return (
    <section className="qm-promo" aria-labelledby={id}>
      <span className="qm-promo__art" aria-hidden="true" />
      <h2 id={id}>{title}</h2>
      <p>{body}</p>
      {action}
    </section>
  );
}

export function Skeleton({ lines = 3, label }: { lines?: number; label: string }) {
  return (
    <div role="status" aria-live="polite" aria-label={label} style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
      {Array.from({ length: lines }, (_, i) => <span key={i} className="qm-skeleton" style={{ inlineSize: `${100 - (i % 3) * 18}%` }} />)}
    </div>
  );
}
